import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";

import { toast } from "react-toastify";
import { getCurrentOrgInfo, fetchChildOrganizations, fetchLocationsByOfficeCodes, viewOrganizationById, viewOrganizationByOfficeCode, deleteOffice, deleteWarehouse } from "../api";
import ConfirmModal from "../../SystemUsers/ConfirmModal";
import { addTab, setActiveTab } from "../../../../redux/slices/manageTeamSlice";
import { selectModuleTabs } from "../../../../redux/selectors/manageTeamTabSelector";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Button from "../../../../components/ACS-Dashboard/ACS-Blocks/Button";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { MoreHorizontal, ChevronRight } from "lucide-react";
import { usePrivilege } from "../../../../guard/usePrivilege";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { useExpandableRows } from "../../../../hooks/useExpandableRows";
import { useRowActionMenu } from "../../../../hooks/useRowActionMenu";
import { getOrganizationDataFromStorage } from "../../../../utils/officeCodeContext";
import {
  groupChildLocationsByParentOfficeCode,
  reconstructLocationList,
  resolveParentLocationId,
} from "../../../../utils/locationHierarchy";

const TABLE_HEADERS = ["Selection", "Action", "Name", "Phone", "Email", "Office Code"];
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const SORTABLE_COLUMNS = {
  "Name": "name",
  "Phone": "phone",
  "Email": "email",
  "Office Code": "officeCode",
};

const AddChildOrganizationTab = () => {
  const { officeCode, orgId: currentOrgId } = getCurrentOrgInfo();
  const dispatch = useDispatch();
  const { tabs } = useSelector(selectModuleTabs("manageTeam"));

  const [orgs,           setOrgs]           = useState([]);
  const [pageSize,       setPageSize]       = useState(PAGE_SIZE_OPTIONS[1]);
  const [page,           setPage]           = useState(1);
  const [sortBy,         setSortBy]         = useState(null);
  const [ascending,      setAscending]      = useState(true);
  const [selected,       setSelected]       = useState([]);
  const [loading,        setLoading]        = useState(false);

  const [warehouses,        setWarehouses]        = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [confirmDelete,     setConfirmDelete]     = useState(null); // { type: 'office'|'warehouse', id, name }
  const [deleting,          setDeleting]          = useState(false);

  const checkboxHeaderRef = useRef(null);

  const { hasPrivilege }  = usePrivilege();
  // officeManagement:CREATE is granted broadly (every role); add/edit/remove for offices and
  // warehouses is admin-only (LGX-1006), gated by the dedicated officeManagement:ADMIN privilege.
  const canAddChildOrg    = hasPrivilege("officeManagement:ADMIN");

  const { isExpanded, toggleExpand } = useExpandableRows();

  const {
    openRowId: openDropdownRow,
    position: dropdownPos,
    dropdownRef,
    handleActionClick,
    closeMenu: closeDropdownMenu,
  } = useRowActionMenu("data-action-trigger");

  const {
    openRowId: openWarehouseRow,
    position: warehouseDropdownPos,
    dropdownRef: warehouseDropdownRef,
    handleActionClick: handleWarehouseActionClick,
    closeMenu: closeWarehouseMenu,
  } = useRowActionMenu("data-warehouse-action-trigger");

  const loadWarehouses = async () => {
    setWarehousesLoading(true);
    try {
      const res = await fetchLocationsByOfficeCodes();
      // GEOGRAPHY rows are internal reference nodes for the rate-shopping graph, never meant
      // to be user-facing (country/state/city/zip, no name) — excluded defensively here too.
      const data = Array.isArray(res.data) ? res.data : [];
      setWarehouses(data.filter((loc) => loc.locationType !== "GEOGRAPHY"));
    } catch {
      toast.error("Failed to fetch facilities.");
    } finally {
      setWarehousesLoading(false);
    }
  };

  useEffect(() => { loadWarehouses(); }, [officeCode]);

  useEffect(() => {
    window.addEventListener("location:refresh", loadWarehouses);
    return () => window.removeEventListener("location:refresh", loadWarehouses);
  }, []);

  // Facilities (warehouses, terminals, or any other location type) nest under an office
  // only when linked via parentId to a location owned by that office — matching officeCode
  // alone isn't enough, per the actual data model (a location's own officeCode doesn't
  // necessarily mean it's a facility of that office; the parent relationship does).
  const warehousesByOfficeCode = React.useMemo(
    () => groupChildLocationsByParentOfficeCode(warehouses),
    [warehouses]
  );

  // A warehouse nests under an office by pointing its parentId at that office's OWN address
  // Location (see groupChildLocationsByParentOfficeCode above) -- so "Add Warehouse" needs that
  // parent Location's locationRef. It's whichever location has no parent of its own and carries
  // this office's officeCode (the office's own POINT record, created via LGX-1006 item #3).
  const officeLocationByOfficeCode = React.useMemo(() => {
    const resolved = reconstructLocationList(warehouses);
    const map = {};
    resolved.forEach((loc) => {
      if (resolveParentLocationId(loc) == null && loc.officeCode) {
        map[loc.officeCode] = loc;
      }
    });
    return map;
  }, [warehouses]);

  // Opens as its own tab (contentType "addLocation", already wired in ManageTeamDetails.js)
  // rather than a modal overlay -- the form (address + contact + weekly calendar sections) is
  // too long to do justice to in a cramped dialog.
  const handleAddWarehouse = (targetOfficeCode) => {
    const parentLocation = officeLocationByOfficeCode[targetOfficeCode];
    if (!parentLocation) {
      toast.warning("This office has no address on file yet — add one before creating a warehouse.");
      return;
    }
    dispatch(addTab({
      id: `add-warehouse-${parentLocation.id}-${Date.now()}`,
      name: `Add Warehouse: ${parentLocation.locationName || targetOfficeCode}`,
      contentType: "addLocation",
      data: {
        locationRef: parentLocation.locationRef,
        officeCode: targetOfficeCode,
        token: Cookies.get("authToken"),
      },
      isDynamic: true,
      createdAt: Date.now(),
    }));
    dispatch(setActiveTab({ index: tabs.length }));
  };

  // Child-org rows never include the current office's own record, so a warehouse
  // belonging to THIS office (not a child org) would otherwise have no row to nest
  // under whenever there are zero child orgs. Always show a "self" row for it.
  const selfOrgRow = React.useMemo(() => {
    if (!officeCode) return null;
    const ownOrgData = getOrganizationDataFromStorage().find((o) => o.officeCode === officeCode);
    const name = ownOrgData?.organizationName || ownOrgData?.officeName || ownOrgData?.name || ownOrgData?.orgName || officeCode;
    return {
      officeCode,
      orgId: currentOrgId,
      name: `${name} (This Office)`,
      phone: ownOrgData?.phone || null,
      email: ownOrgData?.email || null,
      isSelf: true,
    };
  }, [officeCode, currentOrgId]);

  const allOrgRows = React.useMemo(
    // Some office hierarchies return the current office as one of its own "children" —
    // drop that duplicate since selfOrgRow already represents this office.
    () => (selfOrgRow ? [selfOrgRow, ...orgs.filter((o) => o.officeCode !== officeCode)] : orgs),
    [selfOrgRow, orgs, officeCode]
  );

  const handleViewWarehouse = (warehouse) => {
    dispatch(addTab({
      id: `view-location-${warehouse.id}-${Date.now()}`,
      name: `Location: ${warehouse.locationName}`,
      contentType: "viewLocation",
      data: warehouse,
      isDynamic: true,
      createdAt: Date.now(),
    }));
    dispatch(setActiveTab({ index: tabs.length }));
  };

  // Same view (org profile) already used by Preferred Partners — reused here so office rows
  // get a real "View" instead of an empty menu. The self ("This Office") row's numeric id comes
  // from getCurrentOrgInfo() (orgId), since the synthetic self row has no org_id of its own the
  // way a real child-org API record does.
  const handleViewOffice = async (org) => {
    try {
      // Self row: look up by office code (avoids stale org ID in localStorage).
      // Child rows: look up by numeric ID from the API response.
      const res = org.isSelf
        ? await viewOrganizationByOfficeCode(org.officeCode)
        : await viewOrganizationById(org.org_id);
      const tabKey = org.isSelf ? org.officeCode : org.org_id;
      dispatch(addTab({
        id: `view-office-${tabKey}-${Date.now()}`,
        name: `Organization ${org.name}`,
        contentType: "viewPreferredPartnerDetails",
        data: res.data,
        isDynamic: true,
        createdAt: Date.now(),
      }));
      dispatch(setActiveTab({ index: tabs.length }));
    } catch (err) {
      console.error("Failed to load organization details:", err);
      toast.error("Failed to fetch organization details.");
    }
  };

  // LGX-1006: deleting an office is now blocked server-side (with a clear message surfaced via
  // the catch below) if it has a warehouse attached or offices nested under it -- previously
  // neither was actually checked, so a warehouse was silently orphaned and nested offices hit a
  // raw DB constraint error despite this same confirmation dialog claiming a cascade that never
  // really happened. Confirmation copy below now matches that (no cascade claim).
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      if (confirmDelete.type === "office") {
        await deleteOffice(confirmDelete.id);
        toast.success(`${confirmDelete.name} removed.`);
        loadOrgs();
      } else {
        await deleteWarehouse(confirmDelete.id);
        toast.success(`${confirmDelete.name} removed.`);
        loadWarehouses();
      }
    } catch (err) {
      toast.error(err?.response?.data?.content || `Failed to remove ${confirmDelete.name}.`);
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  const handleSort = (header) => {
    const key = SORTABLE_COLUMNS[header];
    if (!key) return;
    if (sortBy === key) {
      setAscending(a => !a);
    } else {
      setSortBy(key);
      setAscending(true);
    }
  };

  const handleSelectAll = (checked) =>
    setSelected(checked ? allOrgRows.map(o => o.officeCode) : []);
  const handleSingleCheckbox = (code) =>
    setSelected(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);

  const loadOrgs = async () => {
    setLoading(true);
    try {
      const res = await fetchChildOrganizations(officeCode);
      setOrgs(Array.isArray(res?.data) ? res.data : []);
    } catch {
      toast.error("Failed to fetch child organizations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrgs(); }, [officeCode]);

  useEffect(() => {
    window.addEventListener("organization:refresh", loadOrgs);
    return () => window.removeEventListener("organization:refresh", loadOrgs);
  }, [officeCode]);

  // Opens as its own tab (contentType "addOffice", wired in ManageTeamDetails.js to render
  // ViewPreferredPartnerDetails in create mode) -- same reasoning as handleAddWarehouse below:
  // one real details-page component instead of a separate throwaway "Add" form (LGX-1006 redo).
  const handleAddOffice = () => {
    dispatch(addTab({
      id: `add-office-${officeCode}-${Date.now()}`,
      name: "Add Office",
      contentType: "addOffice",
      data: { parentOfficeCode: officeCode },
      isDynamic: true,
      createdAt: Date.now(),
    }));
    dispatch(setActiveTab({ index: tabs.length }));
  };

  const sortedOrgs = !sortBy ? allOrgRows : [...allOrgRows].sort((a, b) => {
    const aVal = (a[sortBy] ?? "").toString().toLowerCase();
    const bVal = (b[sortBy] ?? "").toString().toLowerCase();
    return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
  const totalPages = Math.max(1, Math.ceil(sortedOrgs.length / pageSize));
  const paged      = sortedOrgs.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {canAddChildOrg && (
        <div className="flex justify-end p-fluid-2">
          <Button
            text="Add Office"
            onClick={handleAddOffice}
            backgroundColor="#223688"
            textColor="white"
            fontSize="14px"
            padding="10px 18px"
            borderRadius="24px"
          />
        </div>
      )}

      <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
        <Table
          headers={TABLE_HEADERS}
          data={paged}
          onSelectAll={handleSelectAll}
          renderCheckboxActions={() => {}}
          checkboxHeaderRef={checkboxHeaderRef}
          selectedRows={selected}
          onSort={handleSort}
          sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
          sortBy={sortBy}
          ascending={ascending}
          sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
          stickyColumns={[0, 1, 2]}
          renderChildRows={(org) => {
            if (!isExpanded(org.officeCode)) return null;
            const kids = warehousesByOfficeCode[org.officeCode] || [];

            return kids.map((warehouse) => (
              <tr key={`warehouse-${warehouse.id}`} className="bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 [&>td]:py-1.5 [&>td]:text-[11px] sm:[&>td]:text-sm">
                {/* Child checkbox placeholder */}
                <td
                  className="sticky left-0 bg-purple-50 dark:bg-purple-900/20 border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                  style={{ width: "80px", minWidth: "80px", maxWidth: "80px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.08)" }}
                />

                {/* Child action */}
                <td
                  onClick={(e) => e.stopPropagation()}
                  className="sticky left-[80px] bg-purple-50 dark:bg-purple-900/20 border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
                  style={{ width: "50px", minWidth: "50px", maxWidth: "50px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.08)" }}
                >
                  <button
                    data-warehouse-action-trigger
                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                    onClick={(e) => handleWarehouseActionClick(warehouse.id, e)}
                  >
                    <MoreHorizontal size={18} className="mx-auto" />
                  </button>
                </td>

                {openWarehouseRow === warehouse.id && ReactDOM.createPortal(
                  <div
                    ref={warehouseDropdownRef}
                    style={{ position: "absolute", top: warehouseDropdownPos.top, left: warehouseDropdownPos.left, zIndex: 9999 }}
                  >
                    <ActionMenu
                      rowId={warehouse.id}
                      isOpen
                      onClose={closeWarehouseMenu}
                      moduleName="Facility"
                      actions={[
                        { icon: "Eye", label: "View", color: "blue", action: () => handleViewWarehouse(warehouse) },
                        ...(canAddChildOrg
                          ? [{
                              icon: "Trash",
                              label: "Remove",
                              color: "red",
                              action: () => {
                                closeWarehouseMenu();
                                setConfirmDelete({ type: "warehouse", id: warehouse.id, name: warehouse.locationName || "this warehouse" });
                              },
                            }]
                          : []),
                      ]}
                    />
                  </div>,
                  document.body
                )}

                {/* Child "Name" (warehouse location name) */}
                <td
                  className="sticky px-2 sm:px-3 py-1 sm:py-2 bg-purple-50 dark:bg-purple-900/20 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none z-[9]"
                  style={{ left: "130px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "200px", minWidth: "200px", maxWidth: "200px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.08)" }}
                  onDoubleClick={() => handleViewWarehouse(warehouse)}
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 border-l-2 border-b-2 border-gray-500 -ml-1" />
                    <span className="font-medium text-gray-700 dark:text-acs-dark-text truncate">{warehouse.locationName}</span>
                  </div>
                </td>

                {/* Remaining columns — address, matching "Phone"/"Email"/"Office Code" slots with location fields */}
                {[
                  { key: "addressLine1", label: "Address" },
                  { key: "city", label: "City" },
                  { key: "officeCode", label: "Office Code" },
                ].map(({ key }) => (
                  <td
                    key={key}
                    className="px-2 sm:px-3 py-1 text-gray-900 dark:text-acs-dark-text text-xs border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none"
                    style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "200px", minWidth: "200px", maxWidth: "200px" }}
                    onDoubleClick={() => handleViewWarehouse(warehouse)}
                  >
                    {warehouse[key] || "-"}
                  </td>
                ))}
              </tr>
            ));
          }}
        >
          {(org) => {
            const id     = org.officeCode;
            const isOpen = openDropdownRow === id;

            return (
              <>
                {/* Column 0 — Checkbox */}
                <td
                  className="sticky left-0 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                  style={{ width: "80px", minWidth: "80px", maxWidth: "80px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)" }}
                >
                  <input
                    type="checkbox"
                    className="cursor-pointer mx-auto block"
                    checked={selected.includes(org.officeCode)}
                    onChange={() => handleSingleCheckbox(org.officeCode)}
                  />
                </td>

                {/* Column 1 — Action (portal dropdown) */}
                <td
                  onClick={(e) => e.stopPropagation()}
                  className="sticky left-[80px] bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
                  style={{ width: "50px", minWidth: "50px", maxWidth: "50px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)" }}
                >
                  <button
                    data-action-trigger
                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                    onClick={(e) => handleActionClick(id, e)}
                  >
                    <MoreHorizontal size={18} className="mx-auto" />
                  </button>
                </td>

                {isOpen && ReactDOM.createPortal(
                  <div
                    ref={dropdownRef}
                    style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                  >
                    <ActionMenu
                      rowId={id}
                      isOpen
                      onClose={closeDropdownMenu}
                      moduleName="ChildOrg"
                      actions={[
                        { icon: "Eye", label: "View", color: "blue", action: () => handleViewOffice(org) },
                        ...(canAddChildOrg
                          ? [{
                              icon: "FilePlus",
                              label: "Add Warehouse",
                              color: "blue",
                              action: () => {
                                closeDropdownMenu();
                                handleAddWarehouse(org.officeCode);
                              },
                            }]
                          : []),
                        // "This Office" (the self row) is the office currently being managed --
                        // removing it from within its own management view doesn't make sense.
                        ...(canAddChildOrg && !org.isSelf
                          ? [{
                              icon: "Trash",
                              label: "Remove",
                              color: "red",
                              action: () => {
                                closeDropdownMenu();
                                setConfirmDelete({ type: "office", id: org.org_id, name: org.name || "this office" });
                              },
                            }]
                          : []),
                      ]}
                    />
                  </div>,
                  document.body
                )}

                {/* Columns 2+ — Data */}
                {TABLE_HEADERS.slice(2).map((header, i) => {
                  const isSticky = i === 0; // "Name"
                  const rowWarehouses = warehousesByOfficeCode[org.officeCode] || [];
                  let content;
                  switch (header) {
                    case "Name":        content = org.name       || "-"; break;
                    case "Phone":       content = org.phone      || "-"; break;
                    case "Email":       content = org.email      || "-"; break;
                    case "Office Code": content = org.officeCode || "-"; break;
                    default:            content = "-";
                  }
                  return (
                    <td
                      key={header}
                      className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${
                        isSticky ? "sticky bg-white dark:bg-acs-dark-surface z-[9]" : ""
                      }`}
                      style={{
                        left:         isSticky ? "130px" : undefined,
                        boxShadow:    isSticky ? "2px 0 2px -1px rgba(0,0,0,0.1)" : undefined,
                        whiteSpace:   "nowrap",
                        overflow:     "hidden",
                        textOverflow: "ellipsis",
                        width:        "200px",
                        minWidth:     "200px",
                        maxWidth:     "200px",
                      }}
                      onDoubleClick={() => handleViewOffice(org)}
                    >
                      {header === "Name" ? (
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">{content}</span>
                          <span className="flex items-center gap-1 shrink-0">
                            {rowWarehouses.length > 0 && (
                              <span className="text-[10px] text-gray-400 dark:text-acs-dark-subtext">
                                {rowWarehouses.length}
                              </span>
                            )}
                            <ChevronRight
                              size={16}
                              className={`text-[#0c1629] dark:text-acs-dark-text transition-transform duration-200 ${
                                rowWarehouses.length ? "cursor-pointer" : "opacity-20 cursor-default"
                              } ${isExpanded(org.officeCode) ? "rotate-90" : "rotate-0"}`}
                              title={rowWarehouses.length ? (isExpanded(org.officeCode) ? "Hide facilities" : "Show facilities") : "No facilities"}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (rowWarehouses.length) toggleExpand(org.officeCode);
                              }}
                            />
                          </span>
                        </div>
                      ) : content}
                    </td>
                  );
                })}
              </>
            );
          }}
        </Table>
      </div>

      <GlobalPagination
        label="Orgs per page"
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        itemsPerPage={pageSize}
        onItemsPerPageChange={(n) => { setPageSize(n); setPage(1); }}
        options={PAGE_SIZE_OPTIONS}
      />

      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete.type === "office" ? "Remove Office?" : "Remove Warehouse?"}
          message={
            confirmDelete.type === "office"
              ? `This will permanently remove "${confirmDelete.name}". If it has a warehouse attached or offices nested under it, remove those first. This cannot be undone.`
              : `This will permanently remove "${confirmDelete.name}". This cannot be undone.`
          }
          confirmLabel={deleting ? "Removing..." : "Remove"}
          danger
          onConfirm={handleConfirmDelete}
          onCancel={() => !deleting && setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default AddChildOrganizationTab;
